(ns frontend.views.daily-chart
  "Frontend behaviour for the Program Activity page's per-day line charts."
  (:require
   [clojure.string       :as string]
   [common.chart-tooltip :as chart-tooltip]
   [common.util          :as u]
   [opengb.i18n          :refer [tr]]
   [shadow.graft         :as graft]))

(defn- place!
  [^js tooltip-el ^js chart ^js band pointer-y]
  (let [chart-box    (.getBoundingClientRect chart)
        column-box   (.getBoundingClientRect band)
        tooltip-box  (.getBoundingClientRect tooltip-el)
        {:keys [left top]} (chart-tooltip/placement
                            {:chart-left     (.-left chart-box)
                             :chart-top      (.-top chart-box)
                             :chart-width    (.-width chart-box)
                             :column-left    (.-left column-box)
                             :column-width   (.-width column-box)
                             :tooltip-width  (.-width tooltip-box)
                             :tooltip-height (.-height tooltip-box)
                             :pointer-y      pointer-y
                             ;; px of vertical clearance between the pointer and
                             ;; the bottom edge of the tooltip
                             :gap            12})]
    (set! (.. tooltip-el -style -left) (str left "px"))
    (set! (.. tooltip-el -style -top) (str top "px"))))

(defn- band->text
  "The tooltip's content for one day: the date, then what each line counted."
  [^js band-datum]
  (let [[year month day] (.-ymd band-datum)]
    (->> (.-counts band-datum)
         (map (fn [^js line]
                (tr :backend.views.daily-chart/day-line
                    {:LABEL (.-label line) :COUNT (.-count line)})))
         (cons (tr :backend.views.daily-chart/day
                   {:DAY (js/Date. year (dec month) day)}))
         (string/join "\n"))))

(defn- hovered-day-tooltip-handler
  "The chart's Vega tooltip handler: it shows the date the pointer is over and
  what each of the chart's lines counted that day."
  [^js chart]
  ;; vega's own tooltip just writes the raw datum into a title attribute. We
  ;; want to render a custom tooltip element populated by the datum's data
  (let [^js tooltip-el (.querySelector chart "[data-chart-tooltip]")
        hide!          (fn []
                         (set! (.-hidden tooltip-el) true))
        show!          (fn [^js band ^js band-datum pointer-y]
                         (set! (.-textContent tooltip-el) (band->text band-datum))
                         ;; the tooltip has to be shown before it is placed
                         (set! (.-hidden tooltip-el) false)
                         ;; vega positions nothing of its own. this centres
                         ;; the tooltip over the hovered band and floats it
                         ;; above the pointer
                         (place! tooltip-el chart band pointer-y))]
    ;; WCAG 2.1's Content on Hover or Focus asks that content shown on hover be
    ;; dismissable without moving the pointer, which vega has no notion of
    (.addEventListener js/document "keydown"
                       (fn [^js event]
                         (when (= "Escape" (.-key event))
                           (hide!))))
    ;; vega gives us the hovered datum, we render our tooltip using that datum
    (fn [_handler ^js event _item ^js band-datum]
      (if band-datum
        (show! (.-target event) band-datum (.-clientY event))
        (hide!)))))

(defn- render-chart!
  "Draws the Vega spec the backend built into the chart's canvas element."
  [^js chart spec]
  (let [^js canvas (.querySelector chart "[data-chart-canvas]")]
    (-> (js/vega.View. (js/vega.parse (clj->js spec))
                       #js {:renderer "svg"
                            :container canvas
                            :hover     true
                            :tooltip   (hovered-day-tooltip-handler chart)})
        (.runAsync))))

(defmethod graft/scion (u/->scion-id :frontend.views.daily-chart/render-chart)
  [{:keys [spec]} chart]
  (render-chart! chart spec))
